import type { SVGProps } from 'react';

/**
 * Icone della griglia di navigazione della Hub.
 *
 * Disegnate su misura per questa griglia (fornite come SVG, non prese da
 * lucide) e tenute qui invece che come file `.svg` importati: così ereditano
 * `currentColor` e la larghezza del tratto dal bottone che le contiene, e lo
 * stato attivo/inattivo si colora senza duplicare l'asset.
 *
 * Stesso contratto di lucide-react — `className` e `strokeWidth` — così sono
 * intercambiabili con le icone che il resto dell'app usa ancora.
 */

type IconProps = SVGProps<SVGSVGElement> & { strokeWidth?: number | string };

function Icon({ strokeWidth = 2, children, ...props }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {children}
    </svg>
  );
}

/** Ranked — spade incrociate. */
export function RankedSwordsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M21 3v5l-11 9l-4 4l-3 -3l4 -4l9 -11l5 0" />
      <path d="M5 13l6 6" />
      <path d="M14.32 17.32l3.68 3.68l3 -3l-3.365 -3.365" />
      <path d="M10 5.5l-2 -2.5h-5v5l3 2.5" />
    </Icon>
  );
}

/** Eventi — coppa. */
export function EventsTrophyIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M8 21l8 0" />
      <path d="M12 17l0 4" />
      <path d="M7 4l10 0" />
      <path d="M17 4v8a5 5 0 0 1 -10 0v-8" />
      <path d="M3 9a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
      <path d="M17 9a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
    </Icon>
  );
}

/** Sit&Go — saetta. */
export function SitngoBoltIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M13 3l0 7l6 0l-8 11l0 -7l-6 0l8 -11" />
    </Icon>
  );
}

/** Community — cuore. */
export function CommunityHeartIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" />
    </Icon>
  );
}

/** Classifica — barre. */
export function RankingsChartIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 13a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -6" />
      <path d="M15 9a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -10" />
      <path d="M9 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -14" />
      <path d="M4 20h14" />
    </Icon>
  );
}

/** Decks — carte. */
export function DecksCardsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.604 7.197l7.138 -3.109a.96 .96 0 0 1 1.27 .527l4.924 11.902a1 1 0 0 1 -.514 1.304l-7.137 3.109a.96 .96 0 0 1 -1.271 -.527l-4.924 -11.903a1 1 0 0 1 .514 -1.304l0 .001" />
      <path d="M15 4h1a1 1 0 0 1 1 1v3.5" />
      <path d="M20 6c.264 .112 .52 .217 .768 .315a1 1 0 0 1 .53 1.311l-2.298 5.374" />
    </Icon>
  );
}

/**
 * Battaglie — spada singola.
 *
 * Volutamente DIVERSA dalle spade incrociate di Ranked: due sezioni adiacenti
 * con la stessa icona si leggono come la stessa cosa. Esiste anche una variante
 * "cronologia" (`BattlesHistoryIcon`) se si preferisce evocare lo storico
 * invece del combattimento.
 */
export function BattlesSwordIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 4v5l-9 7l-4 4l-3 -3l4 -4l7 -9l5 0" />
      <path d="M6.5 11.5l6 6" />
    </Icon>
  );
}

/** Battaglie — variante "cronologia" (alternativa a BattlesSwordIcon). */
export function BattlesHistoryIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 8l0 4l2 2" />
      <path d="M3.05 11a9 9 0 1 1 .5 4m-.5 5v-5h5" />
    </Icon>
  );
}

/** Live — trasmissione. */
export function LiveBroadcastIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M18.364 19.364a9 9 0 1 0 -12.728 0" />
      <path d="M15.536 16.536a5 5 0 1 0 -7.072 0" />
      <path d="M11 13a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
    </Icon>
  );
}
